import { useEffect, useRef, useState } from 'react'
import type { ClienteDTO, PagaebleResult, SearchProspectoDTO, SearchVendedorDTO, WrapperResponse } from '../model/kartera-api-model'
import APIInvoke from './APIInvoke'

const MIN_LOOKUP_LENGTH = 3
const LOOKUP_DELAY_MS = 350

export type LookupOption = Pick<ClienteDTO, 'id' | 'nombres' | 'apPat' | 'apMat'>

export type LookupInputReason = 'input' | 'reset' | 'clear' | 'blur' | 'selectOption' | 'removeOption'

export interface LookupFieldOptions<T extends LookupOption> {
    value: T | null
    onChange: (value: T | null) => void
    search: (text: string) => Promise<T[]>
}

export const lookupName = (option: Pick<LookupOption, 'nombres' | 'apPat' | 'apMat'>) =>
    [option.nombres, option.apPat, option.apMat].filter(Boolean).join(' ')

const searchItems = async <T>(path: string) => {
    const payload: WrapperResponse<PagaebleResult<T>> | null = await (await APIInvoke.invokeGET(path)).json()
    if (!payload?.ok) return []
    return payload.body?.items || []
}

export const searchClientes = (text: string) =>
    searchItems<ClienteDTO>(`/clientes?nombre=${encodeURIComponent(text)}&size=20&order=nombres%20asc`)

export const searchProspectos = (text: string) =>
    searchItems<SearchProspectoDTO>(`/prospectos?nombres[cn]=${encodeURIComponent(text)}&size=20&order=nombres%20asc`)

export const searchVendedores = (text: string) =>
    searchItems<SearchVendedorDTO>(`/vendedores?nombre[cn]=${encodeURIComponent(text)}&size=20&order=nombres%20asc`)

/** Autocompletar con búsqueda diferida (mín. 3 letras) para filtros de cliente y vendedor. */
export const useLookupField = <T extends LookupOption>({ value, onChange, search }: LookupFieldOptions<T>) => {
    const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const skipSync = useRef(false)
    const [inputValue, setInputValue] = useState(value ? lookupName(value) : '')
    const [options, setOptions] = useState<T[]>([])
    const [searching, setSearching] = useState(false)

    useEffect(() => {
        if (skipSync.current) {
            skipSync.current = false
            return undefined
        }
        setInputValue(value ? lookupName(value) : '')
        return undefined
    }, [value])

    useEffect(() => () => clearTimeout(timer.current), [])

    const changeInput = (next: string, reason: LookupInputReason) => {
        setInputValue(next)
        if (reason === 'reset') return
        clearTimeout(timer.current)
        if (reason === 'clear') {
            setOptions([])
            setSearching(false)
            onChange(null)
            return
        }
        if (reason === 'input' && value) {
            skipSync.current = true
            onChange(null)
        }
        if (next.trim().length < MIN_LOOKUP_LENGTH) {
            setOptions([])
            setSearching(false)
            return
        }
        timer.current = setTimeout(async () => {
            try {
                setSearching(true)
                setOptions(await search(next.trim()))
            } catch {
                setOptions([])
            } finally {
                setSearching(false)
            }
        }, LOOKUP_DELAY_MS)
    }

    return {
        inputValue,
        searching,
        options: value && !options.some((item) => item.id === value.id) ? [value, ...options] : options,
        noOptionsText: inputValue.trim().length < MIN_LOOKUP_LENGTH ? 'Escribe al menos 3 caracteres' : 'Sin resultados',
        changeInput,
    }
}
